import { io, Socket } from 'socket.io-client';
import { getApiUrl } from './env';

export interface ChatMessagePayload {
  id: number;
  userId: number | null;
  username: string;
  message: string;
  tierName: string | null;
  tierLevel: number;
  tierColor: string | null;
  isBot: boolean;
  isModerator: boolean;
  isReviewed: boolean;
  createdAt: string;
}

export interface ChatOnlineCountPayload {
  count: number;
}

export interface ChatHistoryPayload {
  messages: ChatMessagePayload[];
}

export interface ChatSuspendedPayload {
  expiresAt: string | null;
  reason: string;
}

type ChatEventMap = {
  'chat:connected': (data: { success: boolean; userId: number; username: string }) => void;
  'chat:message': (data: ChatMessagePayload) => void;
  'chat:online-count': (data: ChatOnlineCountPayload) => void;
  'chat:history': (data: ChatHistoryPayload) => void;
  'chat:message-deleted': (data: { id: number }) => void;
  'chat:message-reviewed': (data: { id: number }) => void;
  'chat:suspended': (data: ChatSuspendedPayload) => void;
  'chat:unbanned': (data: Record<string, never>) => void;
  'error': (data: { message: string }) => void;
};

class ChatSocketClient {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private isConnecting = false;

  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        const check = setInterval(() => {
          if (!this.isConnecting) {
            clearInterval(check);
            this.socket?.connected ? resolve() : reject(new Error('Connection failed'));
          }
        }, 100);
        return;
      }

      this.isConnecting = true;

      // Use the API URL directly — Socket.io needs http(s):// not ws://
      let backendUrl = getApiUrl();
      if (import.meta.env.VITE_BACKEND_URL) {
        backendUrl = import.meta.env.VITE_BACKEND_URL;
      }

      // Safari ITP blocks third-party cookies (like ALB's AWSALB sticky session cookie)
      // when frontend (www.goldmachine.com) and API (api.goldmachine.com) are on different subdomains.
      // Polling transport requires sticky sessions to work across multiple backend instances.
      // WebSocket transport maintains a persistent connection to a single instance, so it doesn't need sticky sessions.
      // Force WebSocket-only for Safari to avoid the polling + blocked cookie issue.
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const transports: ('websocket' | 'polling')[] = isSafari ? ['websocket'] : ['websocket', 'polling'];

      this.socket = io(backendUrl + '/chat', {
        auth: { token },
        query: { token },
        transports,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: false,
        upgrade: true,
        rememberUpgrade: false,
        autoConnect: true,
      });

      this.socket.on('connect', () => {
        this.isConnecting = false;
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        this.isConnecting = false;
        reject(error);
      });

      this.socket.on('error', () => {
        this.isConnecting = false;
      });

      this.socket.on('disconnect', () => {
        this.isConnecting = false;
      });

      // Forward all events to listeners
      this.setupForwarding();
    });
  }

  private setupForwarding() {
    if (!this.socket) return;
    const events: (keyof ChatEventMap)[] = [
      'chat:connected', 'chat:message', 'chat:online-count',
      'chat:history', 'chat:message-deleted', 'chat:message-reviewed',
      'chat:suspended', 'chat:unbanned', 'error',
    ];
    events.forEach(event => {
      this.socket?.on(event, (data: any) => {
        this.listeners.get(event)?.forEach(fn => {
          try { fn(data); } catch { /* ignore */ }
        });
      });
    });
  }

  on<K extends keyof ChatEventMap>(event: K, callback: ChatEventMap[K]): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
    return () => this.off(event, callback);
  }

  off<K extends keyof ChatEventMap>(event: K, callback: ChatEventMap[K]) {
    this.listeners.get(event)?.delete(callback);
  }

  send(message: string): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        resolve({ success: false, error: 'Not connected' });
        return;
      }
      this.socket.emit('chat:send', { message }, (response: any) => {
        resolve(response || { success: true });
      });
    });
  }

  requestHistory() {
    if (this.socket?.connected) {
      this.socket.emit('chat:history');
    }
  }

  flag(messageId: number): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        resolve({ success: false, error: 'Not connected' });
        return;
      }
      this.socket.emit('chat:flag', { messageId }, (response: any) => {
        resolve(response || { success: true });
      });
    });
  }

  unflag(messageId: number): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        resolve({ success: false, error: 'Not connected' });
        return;
      }
      this.socket.emit('chat:unflag', { messageId }, (response: any) => {
        resolve(response || { success: true });
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.isConnecting = false;
  }

  get connected(): boolean {
    return this.socket?.connected || false;
  }
}

export const chatSocketClient = new ChatSocketClient();
